window.dn = window.dn || {};

(function (chat, $, undefined)
{
    /**
     * Store information we know about each conversation. Each key will be a DiscussionKey for a
     * conversation, mapped to a dictionary of the form:
     * 
     * {
     *     BottomViewMonitor: <object>,     // "Bottom of conversation" monitor for scrollMonitor
     *     ContainerMonitor: <object>,      // Container monitor for this conversation's scrollMonitor
     *     EarliestMessageKey: <string>,    // MessageKey of the earliest reply we've loaded
     *     EarliestSequence: <int>,         // Sequence of the earliest reply we've loaded
     *     LatestMessageKey: <string>,      // MessageKey of the latest reply we've loaded
     *     LatestSequence: <int>,           // Sequence of the latest reply we've loaded
     *     LastLoadBottom: <int>,           // Timestamp since we last loaded messages at the bottom
     *     LastLoadTop: <int>,              // Timestamp since we last loaded messages at the top
     *     ScrollTop: <int>                 // Stores the scrollTop of the conversation panel as it goes out of view
     *     TopViewMonitor: <object>         // "Top of conversation" monitor for scrollMonitor
     * }
     */
    chat.Conversations = {};

    /**
     * Store an $element reference to the currently visible conversation panel (right side).
     * This gets set when it's displayed. We also use this to determine the previously visible
     * conversation that is being hidden (if any) once a new conversation is selected, so we 
     * can do things like store the current scrollTop, etc. 
     */
    chat.$CurrentConversation = null;

    /**
     * For tracking our paging through recent chat conversations.
     */
    chat.CurrentPageChatList = 0;

    /**
     * A dictionary of editor elements, mapping DiscussionKeys to editor elements, which will 
     * either be an instance of CKEditor, or a textarea. Suitable for calling .setData(), 
     * .getData() on (if rich text editing), or .val() on otherwise.
     */
    chat.Editors = {};

    /**
     * This gets set to false if the user reaches the end of their available chat conversations,
     * so that we won't try to fetch any more for the given search criteria. The dictionary index
     * is the current keywords being searched, e.g. chat.Keywords.
     */
    chat.HasMoreConversations = 
    {
        null: true
    };

    /**
     * Can be set to true in the querystring, ro=1, to hide the posting area of a chat.
     */
    chat.IsReadOnly = false;

    /**
     * Will be set to true in Chat.cshtml if this is a robot conversation PM type.
     */
    chat.IsRobotConversation = false;

    /**
     * What keywords are to be used for searching conversations, if any.
     */
    chat.Keywords = null;

    /**
     * This is our estimate as to the minimum height a chat message takes up,
     * if it's only one line high. We use this in some parts to calculate how
     * many messages we should load. For example, when the conversation is first
     * loaded, we look at the height of the viewport, and divide it by this number,
     * then add 1, and load that many messages to try and fill the viewport.
     */
    chat.MessageMinimumHeight = 35;

    /**
     * Tracking message sends that are currently in progress, mainly so we can
     * try to prevent people from leaving the page while some are in progress.
     */
    chat.MessageSendsInProgress = 0;

    /**
     * How many milliseconds should we monitor viewport sizing after we start
     * inserting replies? Longer is probably fine, but might be a slight performance
     * hit if unnecessarily long. 
     */
    chat.MillisecondsOfResizing = 10000;

    /**
     * How many milliseconds until we remove a typing indicator automatically.
     * If the user starts typing again and we get a new Chat/Typed real-time
     * notification, then we keep the bubble for this additional period of time.
     */
    chat.MillisecondsUntilDoneTyping = 10000;

    /**
     * How many milliseconds until we remove a typing indicator automatically FOR A ROBOT'S RESPONSE.
     * If the user starts typing again and we get a new Chat/Typed real-time
     * notification, then we keep the bubble for this additional period of time.
     */
    chat.MillisecondsUntilDoneTypingRobot = 120000;

    /**
     * Milliseconds between loading more messages. 
     */
    chat.MillisecondsUntilLoadMore = 3000;

    /**
     * The number of conversations in the view that are currently muted.
     */
    chat.MutedConversations = {};

    /**
     * The namespace for storage
     */
    chat.Namespace = "dn.chat.";

    /**
     * The number of replies we want to load as the user "pages" by scrolling.
     */
    chat.NumRepliesPerPage = 20;

    /**
     * The message to display in the message input box when it's empty.
     */
    chat.Placeholder = "Type your message, hit Enter to send. Start a new line with Ctrl+Shift.";

    /**
     * Gets set to an int value when we are expecting the conversation viewport to be resizing
     * due to additional messages being inserted. Refer to chat.StartResizing() for details.
     */
    chat.ResizingTimer = 0;

    /**
     * Track the total conversations loaded in the left side bar.
     */
    chat.TotalConversations = 0;

    /**
     * Track the total conversations loaded in the left side bar.
     */
    chat.TotalAvailableConversations = 0;

    /**
     * If we're sitting on the chat view, this will be set to the type of chat we're viewing.
     * Otherwise it's set to null.
     */
    chat.Type = null;

    /*
     * How many milliseconds do we wait after typing to check if the submit button(s) are activated.
     */
    chat.WaitForTyping = 500;

    /**
     * This is a method called during animation frames, but only while we're expecting 
     * resizing to occur on the chat viewport. Refer to chat.StartResizing() for details
     * on when/why `chat.ResizingTimer` will be non-zero, which is a requirement 
     * for this method to execute during an animation frame. 
     */
    chat.AdjustScrollPosition = function() 
    {
        if (chat.ResizingTimer !== 0 && chat.$CurrentConversation != null)
        {
            var discussionKey = chat.$CurrentConversation.data("discussionkey");
            var newHeight = chat.$CurrentConversation[0].firstElementChild.getBoundingClientRect().height;

            if (newHeight !== chat.Conversations[discussionKey].PreviousHeight)
            { 
                // If height changed, reset our timer to disable resize monitoring for another 10 seconds
                chat.StartResizing();

                // Now reset our scrollTop position so the viewer doesn't notice movement
                var newScrollTop = chat.$CurrentConversation.scrollTop() + newHeight - chat.Conversations[discussionKey].PreviousHeight;
                chat.Conversations[discussionKey].PreviousHeight = newHeight;
                chat.$CurrentConversation.scrollTop(newScrollTop);
            }

            // Start the request for an animation frame over again
            requestAnimationFrame(chat.AdjustScrollPosition);
        }
    };

    /**
     * This takes a list of Author objects and checks whether or not they need to be 
     * marked as muted, disabled, or staff. Currently it just targets the anchor element
     * of the avatar so that it doesn't mess with the anchor - but other places might need 
     * to change this in some way to allow the target selector to be more dynamic.
     */
    chat.ApplyAuthorIcons = function(authorCollection)
    {
        authorCollection.forEach
        ( 
            function(author)
            {
                var key = author.UserKey;

                // Skip the system user
                if (author.UserKey != dn.constants.CommunitySuperUserKey)
                {
                    if (!author.IsActive)
                    {
                        dn.user.AppendAvatarIcon(".avatar-" + author.UserKey + " a", dn.user.AvatarIcons.Deactivated, true)
                    }

                    // if (author.IsAdministrator)
                    // {
                    //     dn.user.AppendAvatarIcon(".avatar-" + author.UserKey + " a", dn.user.AvatarIcons.Staff, true)
                    // }

                    // If the muted users collection includes the author's UserKey, and the mute settings doesn't allow to ConversationShow
                    if ((author.UserKey in dn.user.MutedUsers) && !dn.HasFlag(dn.user.MutedUsers[author.UserKey], dn.constants.MuteFlags.ConversationShow))
                    {
                        dn.user.AppendAvatarIcon(".avatar-" + author.UserKey + " a", dn.user.AvatarIcons.Mute, true);
                    }
                }
            }
        )
    }

    /**
     * This calculates the recommended number of messages to load to at least fill
     * the current viewport. This is done by taking the current viewport size, and 
     * dividing by the minimum message height. Typically this will yield more messages
     * than are needed, but is more performant than having to call the messages 
     * API multiple times.
     */
    chat.CalculateMessageCountForViewport = function()
    {
        return (Math.round($(window).height() / chat.MessageMinimumHeight) + 1);
    };

    /**
     * Internal function called when something has changed on the `StartConversation` form and
     * we want to see whether or not we should enable the "Send Message" button, among other things.
     */
    var checkEnableSendPrivateMessageButton = dn.Debounce
    (
        function()
        {
            console.debug("chat.checkEnableSendPrivateMessageButton", $('#dn-chat-start-conversation-user-search').val(), (dn.constants.UseRichTextEditor ? chat.Editors["dn-chat-start-conversation-body"].getData() : chat.Editors["dn-chat-start-conversation-body"].val()));

            // Enable the Send button if there's a title, body, and recipients.
            $(".dn-chat-start-conversation-send-button").prop
            (
                "disabled", 
                !(
                    // Recipients AND
                    $('#dn-chat-start-conversation-user-search').val() && 
                    // Message body
                    (dn.constants.UseRichTextEditor ? chat.Editors["dn-chat-start-conversation-body"].getData() : chat.Editors["dn-chat-start-conversation-body"].val())
                )
            );
        },
        100,    // wait 100ms
        false   // execute after the first time period passes
    );

    /**
     * Delete the chat from the user's view. It will appear if someone re-sends a message
     * to the chat. 
     */
    chat.Delete = function(discussionKey, shouldAlsoLeave)
    {
        $.deleteJSON(
            "/api/v1/chat/" + discussionKey,
            { "Leave": shouldAlsoLeave }
        )
        .done
        (        
            function()
            {
                chat.RemoveFromClient(discussionKey);
            }            
        )
        .fail
        (
            function (xhr, textStatus, errorThrown) 
            {
                if (!dn.IsErrorHandledAutomatically(null, xhr, null, errorThrown)) 
                {
                    console.error("Leave Failure", xhr, textStatus, errorThrown);
                    dn.ShowModal({ title: "Error Leaving Conversations", body: dn.FormatApiError(xhr.responseJSON) });
                }
            }
        );
    };

    /**
     * If there is a pending draft, grab it and load it into the container
     * @param {string} discussionKey - The key for the editor to use to store the values in/on
     */
    // TODO: We need to get dn.crypto working, including safely working around issues with
    // older browsers that don't support async/await. We had commented this out earlier as well
    // because we found some underling issues with our crypto implementation. See:
    // 
    //  https://github.com/DiagnosticNetwork/code/issues/1193
    //
    // chat.GetDraftForDiscussion = async function(discussionKey)
    // {
    //     var storedMessage = await dn.storage.RetrieveLocal(dn.user.UserKey, chat.Namespace + discussionKey)
    //     if (storedMessage)
    //     {
    //           // Clear the typing field
    //         if (dn.constants.UseRichTextEditor) 
    //         {
    //             chat.Editors[discussionKey].setData(storedMessage);
    //         } 
    //         else 
    //         {
    //             chat.Editors[discussionKey].val(storedMessage);
    //         }
    //     }
    // };

    /**
     * Removes highlighting on any existing highlighted conversations, and highlights
     * the conversation specified with the given discussionKey. Typically this would
     * be called once a conversation is clicked, or if one is being loaded directly
     * when the chat view is first loaded, or after StartConversation is called.
     * 
     * @param {string}  discussionKey   - The conversation to highlight.
     */
    chat.HighlightConversation = function(discussionKey)
    {
        console.debug("chat.HighlightConversation", discussionKey);

        // Update the header to show the participants of the highlighted conversation
        $(".dn-chat-recipients").hide();
        $("#dn-chat-recipients-" + discussionKey).show();

        // Remove any existing highlighting
        $(".dn-chat-list-conversation.active").removeClass("active");

        // Add highlighting to the given discussionKey
        $("#dn-chat-conversation-" + discussionKey).addClass("active");
    };

    /**
     * Highlight the unread conversations in the list.
     */
    chat.HighlightUnreadConversations = function(metadata)
    {
        console.debug("chat.HighlightUnreadConversations", metadata);

        for (let key in metadata)
        {
            var conversationMetadata = metadata[key];
            if (conversationMetadata["NumNewReplies"] > 0)
            {
                chat.MarkConversationUnread(key, conversationMetadata["NumNewReplies"]);
            }
        }
    };

    /**
     * The initialization function called when the private messaging page opens.
     */
    chat.Init = function()
    {
        if (Modernizr.backdropfilter)
        {
            // This browser supports backdrop-filter, so activate our styles that provide translucency/blur to chat UI
            $("#dn-chat-list-header, #dn-chat-conversation-header, #dn-chat-entry").addClass("dn-chat-blur-effect");
        }

        // Register a handler for when this user receives a typing notification.
        dn.realtime.RegisterHandler
        (
            dn.constants.EventCategory.Chat,
            dn.constants.EventAction.Typed,
            function(message)
            {
                // See if we're on a PM page and the message is of the same TypeId, before inserting.
                if (window.location.href.startsWith(dn.constants.DiscussionTypes[message.Data.Message.TypeId].Link))
                {
                    // A notification that someone in this conversation is typing
                    dn.chat.ShowUserTyping(message.Data.Message.DiscussionKey, message.Data.Message.Author);
                }
            }
        );

        if (chat.Type.ViewOptions.HideChrome)
        {
            console.debug("chrome=0", "No links will be followed to internal resources.");

            $(document).on
            (
                'click', 'a', function(event) 
                {
                    // If they're clicking a link that is on our site, but the link does not match
                    // the beginning of the current page, then prevent. This is to prevent navigation
                    // to other pages on the site, while still allowing external links.
                    if ($(this).attr('href').indexOf(dn.constants.BaseHref) === 0 || $(this).attr('href').indexOf("/") === 0)
                    {
                        event.preventDefault(); // Prevent navigation
                        console.debug("chrome=0", "Blocked link to internal resource", $(this).attr('href'));
                    }
                }
            );
        }

        // Try to prevent people from leaving page while we're sending messages
        $(window).bind('beforeunload', function()
        {
            if (chat.MessageSendsInProgress > 0)
            {
                return 'Leave the page while message send(s) are in progress?';
            }
        });

        $(document).on
        (
            "click", 
            ".dn-send-reply",
            function(event)
            {
                // Send current reply
                chat.SendReply($("#dn-chat-reply-form .dn-chat-reply:visible").data("discussionkey"));
            }
        );

        $(document).on
        (
            "click", 
            "#dn-chat-leave-conversation",
            function(event)
            {
                // Leave group chat
                event.preventDefault();
                dn.ShowModal
                ({
                    title: 'Leave Conversation', 
                    body: '<div>\
                                <p>This removes it from your view, and notifies the other participants you\'ve left. If they continue chatting, you will not see it.</p>\
                                <p>The conversation will be deleted from our servers if all participants leave or delete it.</p>\
                            </div>',
                    buttonText: "Leave",
                    buttonClass: "btn-danger",
                    onButtonClick: function (e) 
                    {
                        if (dn.chat.$CurrentConversation)
                        {
                            var discussionkey = $(dn.chat.$CurrentConversation[0]).data("discussionkey"); 
                            dn.chat.Delete(discussionkey, true);
                        }
                    },

                    closeButtonText: "Cancel"
                });
            }
        );

        $(document).on
        (
            "click", 
            "#dn-chat-delete-conversation",
            function(event)
            {
                // Delete chat
                event.preventDefault();
                dn.ShowModal
                ({
                    title: 'Delete Conversation', 
                    body: '<div>\
                                <p>This removes it from your view until another participant sends a new message.</p>\
                                <p>The conversation will be deleted from our servers if all participants leave or delete it.</p>\
                            </div>',
                    buttonText: "Delete",
                    buttonClass: "btn-danger",
                    onButtonClick: function (e) 
                    {
                        if (dn.chat.$CurrentConversation)
                        {
                            var discussionkey = $(dn.chat.$CurrentConversation[0]).data("discussionkey"); 
                            dn.chat.Delete(discussionkey, false);
                        }
                    },
                    closeButtonText: "Cancel"
                });
            }
        );

        // For small devices, there is a chevron displayed when viewing a conversation, letting the
        // user go back and select a different conversation (or start a new one). 
        $(".dn-show-chat-list").click
        (
            function()
            {
                $(".dn-chat-list").addClass("shown");
                $("#dn-chat-entry").hide();
            }
        );

        if (dn.chat.DiscussionKey)
        {
            // Load latest conversations, selecting the one indicated by DiscussionKey
            chat.LoadConversations(dn.chat.DiscussionKey);

            // Initial message specified to load now
            chat.LoadMessages
            (
                dn.chat.DiscussionKey, 
                {
                    NumBefore: chat.CalculateMessageCountForViewport()
                }
            );
        }
        else
        {
            // Load latest conversations
            chat.LoadConversations();

            $('.dn-chat-show-if-conversation').hide();
            $('.dn-chat-hide-if-conversation').show();

            if ( $("#dn-small-screen-open-conversation-list").is(":visible"))
            {
                $("#dn-chat-conversation-header").show()
                $(".dropdown").hide()

                // Show the conversation list if we're loading the PM
                // page without a specific conversation, so that we're
                // showing the convo list panel on small screens.
                $(".dn-chat-list").addClass("shown");
            }

            // If URL contains action=new and a "recipients" recipient list, start a new conversation
            if (dn.QueryString["action"] === "new" && dn.QueryString["recipients"]) 
            {
                chat.UpsertConversation
                ({
                    Recipients: ($.isArray(dn.QueryString.recipients) ? dn.QueryString.recipients : [dn.QueryString.recipients]),
                    OnSuccess: function(result) 
                    {
                        // Insert/replace the new conversation and show it
                        chat.InsertConversation(result.Message);

                        if (dn.QueryString["body"])
                        {
                            // If there's a body specified, pass a callback into ShowConversation to set the body
                            // after the conversation is loaded.
                            chat.ShowConversation(result.Message.DiscussionKey, result.Message.MessageKey, function()
                            {
                                if (dn.constants.UseRichTextEditor)
                                {
                                    chat.Editors[result.Message.DiscussionKey].setData(dn.QueryString["body"]);
                                }
                                else
                                {
                                    chat.Editors[result.Message.DiscussionKey].val(dn.QueryString["body"]);
                                }
                                
                                // Now put the focus in the reply box.
                                $("#dn-chat-reply-body-" + result.Message.DiscussionKey).focus();
                        
                                setTimeout(
                                    function()
                                    {
                                        // and move the cursor to the end of the pre-filled message
                                        chat.Editors[result.Message.DiscussionKey].model.change(function(writer) {
                                            writer.setSelection( writer.createPositionAt( chat.Editors[result.Message.DiscussionKey].model.document.getRoot(), 'end'));
                                        } );
                                    }, 
                                    50
                                );
                            });
                        }
                        else
                        {
                            // No body specified, just show the conversation
                            chat.ShowConversation(result.Message.DiscussionKey, result.Message.MessageKey);
                        }
                    }
                });
            }
        }

        // Setup the "start conversation" button(s)
        $(".dn-chat-compose-button:not(.dn-event-click)").click
        (
            function(e) 
            {
                var $button = $(this);

                if (chat.IsRobotConversation)
                {
                    // Just start the new conversation directly, no modal dialog
                    chat.UpsertConversation
                    ({
                        Recipients: [dn.chat.Type.Robot.UserKey],
                        OnSuccess: function(result) 
                        {
                            // Insert/replace the new conversation and show it
                            chat.InsertConversation(result.Message);
                            chat.ShowConversation(result.Message.DiscussionKey, result.Message.MessageKey);
                        }
                    });
                }
                else
                {
                    // Show the modal dialog for starting a new conversation
                    dn.chat.StartConversation
                    (
                        {
                            body: ($button.data("body") || "")
                        }
                    );
                }
            }
        )
        .addClass("dn-event-click");

        requestAnimationFrame(chat.AdjustScrollPosition);

        if (dn.compose.FileUploader === undefined)
        {
            dn.compose.InitFileUploader();
        }
    };

    /**
     * Updates the target container to reflect the current attachments for the provided
     * discussion (conversation). The attachments would have been put there via other means 
     * and we are just looking to see what's put there, and then build UI controls out of
     * what we find.
     * 
     * Note: This is similar to the existing code in compose.js but we don't share all of the 
     * options on the images. If desired, adding them back should be easy enough, but I don't 
     * think they are needed here.
     * 
     * @param {string} discussionKey - The key for the editor to use to store the values in/on
     * @param {string} targetContainerSelector - The container's selector where to put the attachments (some not created initially)
     */
    function embedAttachmentsToPendingMessage(discussionKey, targetContainerSelector)
    {
        // We are going to store everything on the editor until we are ready to send it that way
        // we don't get overlap or confusion with chat.Conversation['xxx'].FileKeys vs pending ones
        var messageKey = chat.Editors[discussionKey].TemporaryKey;
        var fileKeys = chat.Editors[discussionKey].FileKeys;
        var fileTypes = chat.Editors[discussionKey].FileTypes;

        if (fileKeys && fileKeys.length > 0)
        {
            $.each(fileKeys, function(index, fileKey) 
            {
                // Only add files one time, no matter how many times we call this method.
                if ($('#' + fileKey).length <= 0)
                {
                    var embedFile = null;
                    var fileType = parseInt(fileTypes[index]);

                    if ((fileType & dn.constants.FileTypes.Image) != 0)
                    {
                        // image
                        embedFile = 'data-src="/file/' + fileKey + '/lg"';
                    }
                    else
                    {
                        // something other than image (use /embed)
                        embedFile = 'data-type="iframe" data-src="/file/' + fileKey + '/embed"';
                    }

                    var $image = $('<div id="' + fileKey + '" data-fancybox-trigger="gallery-edit-' + messageKey + '" ' + embedFile + ' data-filetype="' + fileType + '" class="dn-file-wrapper d-inline-block' + (fileType == 32 ? ' video-thumbnail-overlay' : '') + '" ondragstart="dn.compose.DragEvent = event;"><img src="/file/' + fileKey + '/sm" class="img-fluid mh-100 rounded"/></div>');

                    $image.click(function(e) 
                    {
                        e.preventDefault();
                        e.stopPropagation();
                        $image.contextMenu({ x: e.pageX, y: e.pageY });
                    });

                    // hijack both left and right click on attached file thumbnail to enable various functions
                    $.contextMenu
                    (
                        {
                            selector: '#' + $image.attr('id'),
                            callback: function (key, options) 
                            {
                            },
                            items: 
                            {
                                "view":   // preview file
                                {
                                    name: 'View File',
                                    isHtmlName: true,
                                    callback: function (itemKey, opt, e) 
                                    {
                                        // For in-progress videos, show a modal dialog to help the user understand what's going on. 
                                        // For all others, show the file in our overlay/carousel.
                                        dn.ShowFileInGallery($(this), "<p>You can post the message whenever you're ready, and we'll finish processing the video as quickly as possible.</p>");
                                    }
                                },
                                "delete":   // delete file
                                {
                                    name: 'Remove File',
                                    isHtmlName: true,
                                    callback: function () 
                                    {
                                        removeFile(fileKey, targetContainerSelector);
                                    }
                                }
                            }
                        }
                    );

                    $(targetContainerSelector).append($image);
                }
            });
            $(targetContainerSelector).addClass("has-attachments");
        }
        else
        {
            $(targetContainerSelector).removeClass("has-attachments");
        }
    }

    /**
     * Generates a random temporary key for a new message
     * @param {string} discussionKey - The key for the editor to use to store the values in/on
     * @param {string} temporaryKey - Normally this is generated within this function, but if you
     * want to override it for some reason, e.g. if you have a message key of an existing draft
     * message, you can pass it in.
     */
    function getRandomTempKey(discussionKey, temporaryKey)
    {
        if (!temporaryKey)
        {
            temporaryKey = "temp" + ("" + Math.random()).substring(2);
        }

        chat.Editors[discussionKey].TemporaryKey = temporaryKey;
    }

    /**
     * Insert the given conversation into the view (or update it if it already exists).
     * If we're not on the chat view, then we do nothing.
     * 
     * @param {object}  conversation    - The object with all the relevant data for this conversation.     
     */
    chat.InsertConversation = function(conversation)
    {
        if (!chat.IsChatView())
        {
            console.debug("chat.InsertConversation", "Aborting since we're not on the chat view.");
            return;
        }

        var nonSelfAuthors = conversation.Participants.filter
        (
            function(participant)
            {
                return (participant.UserKey !== dn.user.UserKey);
            }
        );

        var displayProfilePhotos = '';
        var namesOrTitle = '';
        var headerParticipants = [];

        var avatars = [];
        var firstnames = [];
        var $header = $('<ul class="dn-chat-recipients" id="dn-chat-recipients-' + conversation.DiscussionKey + '" data-participants-count="' + conversation.Participants.length + '"></ul>');

        // Only start looking for mute relationships if there are any && then just check for pm's - ignoring group messages
        if (dn.user.MutedUsers && (nonSelfAuthors.length === 1))
        {
            var author = nonSelfAuthors[0];

            if (author.UserKey in dn.user.MutedUsers && !dn.HasFlag(dn.user.MutedUsers[author.UserKey], dn.constants.MuteFlags.ConversationShow))
            {
                console.debug("Muted Conversation", author, conversation);
                chat.MutedConversations[conversation.DiscussionKey] = true;
            }
        }

        if (!conversation.LatestMessage)
        {
            // No real LatestMessage metadata, so let's try to fake it?
            if (conversation.Messages)
            {
                conversation.LatestMessage = conversation.Messages[0];
            }
            else
            {
                conversation.LatestMessage = conversation;
            }

            conversation.LatestMessage.FirstName = conversation.LatestMessage.Author.FirstName;
            conversation.LatestMessage.LastName = conversation.LatestMessage.Author.LastName;
            conversation.LatestMessage.UserKey = conversation.LatestMessage.Author.UserKey;
        }

        var snippet = (conversation.LatestMessage && conversation.LatestMessage.Snippet)
            ? ((conversation.LatestMessage.UserKey === dn.user.UserKey ? "You" : conversation.LatestMessage.FirstName) + ": " + conversation.LatestMessage.Snippet)
            : "";

        if (dn.chat.IsRobotConversation)
        {
            // Robot conversations have a title instead of a list of participants
            namesOrTitle = (conversation.Title || '');
            $header.append('<span class="dn-conversation-title" id="dn-conversation-title-' + conversation.DiscussionKey + '">' + namesOrTitle + '</span>');

            // If no title yet, just get the snippet from the latest message to show in the left column
            namesOrTitle = namesOrTitle || conversation.LatestMessage.Snippet;
        }
        else
        {
            for (var i = 0; i < nonSelfAuthors.length; i++)
            {
                firstnames.push(nonSelfAuthors[i].FirstName);
                avatars.push("/u/" + nonSelfAuthors[i].UserKey + "/photo");
                headerParticipants.push('<li><a href="/u/' + nonSelfAuthors[i].UserKey + '" target="' + dn.constants.LinkTargetNewWindow + '" title="' + nonSelfAuthors[i].FirstName + ' ' + nonSelfAuthors[i].LastName + '"><span class="dn-chat-firstname">' + nonSelfAuthors[i].FirstName + '</span> <span class="dn-chat-lastname">' + nonSelfAuthors[i].LastName + '</span></a></li>');
            }

            if (nonSelfAuthors.length > 4)
            {
                // Overwrite the 4th avatar with a "..."
                avatars[3] = dn.constants.UrlWebAssets + "img/avatars/ellipsis.png";
            }

            displayProfilePhotos = '<span class="avatar"><img alt="" src="' + avatars.join('" /></span><span class="avatar"><img alt="" src="') + '" /></span>';
            namesOrTitle = dn.text.JoinForHumans(firstnames, ', ', ', & ', ' & ');

            // Generate a <ul> list of other participants, for the header area of the conversation display.
            // We have a special CSS styling using `li:first-child:nth-last-child` technique to hide last names
            // automatically when there are "a lot."
            // 
            // Or if this type has a RobotUserId, then show the title instead.
            $header.append(dn.text.JoinForHumans(headerParticipants, ', ', ', & ', ' & '));
        }

        var conversationId = "dn-chat-conversation-" + conversation.DiscussionKey;
        var latestMessageDate = (conversation.LatestMessage.DateLastReplied || conversation.LatestMessage.DateCompleted);
        var formattedDate = '';

        if (latestMessageDate)
        {
            formattedDate = dn.FormatDateHtml(moment(latestMessageDate).locale('en-short'), 'Latest: ');
        }
        else
        {
            // No date completed or date last replied, so just show "New Message" since it
            // seems to be the start of a conversation.
            namesOrTitle = '<span style="font-style: normal; font-weight: 500">New Message</span>';
        }

        var $conversation = 
        $(
            '<a href="' + dn.chat.Type.Link + '/' + conversation.DiscussionKey + '" class="dn-chat-list-conversation clickable ' + (chat.MutedConversations[conversation.DiscussionKey] ? "dn-muted-conversation d-none" : "d-flex") + '" id="' + conversationId + '" data-discussionkey="' + conversation.DiscussionKey + '" data-messagekey="' + (conversation.MessageKey || (conversation.Messages ? conversation.Messages[0].MessageKey : "")) + '" data-latest="' + (latestMessageDate || dn.constants.DateMaxValue) + '">\
                <div class="dn-chat-avatars num-avatars-' + nonSelfAuthors.length + '">' + displayProfilePhotos + '</div>\
                <div class="dn-chat-names-timestamp-snippet pl-2">\
                    <div class="dn-chat-names-timestamp">\
                        <div class="dn-chat-names">' + namesOrTitle + '</div>\
                        <div class="dn-chat-timestamp text-small">' + formattedDate + '</div>\
                        <div class="dn-chat-unread-indicator" style="display: none;"></div>\
                    </div>\
                    <div class="d-flex dn-chat-snippet">' + snippet + '</div>\
                </div>\
            </a>'
        )
        .click
        (
            // When they click on the conversation, we cancel the standard link handling,
            // and instead call chat.ShowConversation() with the selected discussion/messagekey.
            // If the conversation has already been loaded, we treat the additional click 
            // as a signal to scroll to the bottom of the discussions.
            function(e)
            {
                e.preventDefault();

                chat.ShowConversation
                (
                    $(this).data("discussionkey"), 
                    $(this).data("messagekey")
                );
            }
        );

        // Remove any pre-existing participants list for this conversation, hide our newly
        // generated participants list, and then append it to the holding area. Later we'll 
        // unhide it whenever we're showing this conversation.
        $("#dn-chat-recipients-" + conversation.DiscussionKey).remove();
        $("#dn-chat-recipients").append($header.hide());
    
        if ($("#" + conversationId).length > 0)
        {            
            // Conversation exists, replace it
            console.debug("chat.InsertConversation", conversation.DiscussionKey, "Replacing");
            $("#" + conversationId).replaceWith($conversation);
        }
        else
        {                
            // New conversation, insert it.
            chat.TotalConversations++;
            console.debug("chat.InsertConversation", conversation.DiscussionKey, "Inserting");
            dn.InsertSorted($("#dn-chat-list-conversations"), $conversation, function ($a, $b) { return $a.data("latest").localeCompare($b.data("latest")); });
        }    
    };

    /**
     * Add a reply to the display of the current discussion. (This function just adds 
     * the reply in the UI, it has nothing to do with posting the reply to the API.)
     * 
     * If `temporaryKey` is passed in, it signals that we previously inserted this reply
     * before we knew it's final MessageKey. We'll replace that previous reply with the 
     * new one from the server (whose body could possibly be different).
     * 
     * @param {string}      discussionKey   - The unique identifier of the conversation.
     * @param {messages}    messages        - The message(s) to be inserted.
     * @param {string}      temporaryKey    - If given, we'll look for this instead of the MessageKey.
     * @param {string}      snippet         - If set, we'll update the snippet that appears in the conversation list.
     */
    chat.InsertMessages = function(discussionKey, messages, temporaryKey, snippet, shiftToTop, markUnread)
    {
        console.debug("chat.InsertMessages", messages[0].MessageKey, temporaryKey, messages);

        // See if it's a robot chat type that might need the title set
        if (dn.chat.IsRobotConversation && messages[0].Title)
        {
            // Set the title in the conversation header
            $("span#dn-conversation-title-" + discussionKey).text(messages[0].Title);

            // Set the title in the conversation list view
            $("#dn-chat-conversation-" + discussionKey + " div.dn-chat-names").text(messages[0].Title);
        }

        if ($("#dn-chat-conversation-" + discussionKey).length === 0)
        {
            // Conversation does not exist yet in our view, so insert it.
            // We first need to retrieve this discussion so that we have something
            // to insert into the left side bar.
            $.postJSON
            (
                "/api/v1/chat/" + discussionKey, 
                {
                    // Only pass MessageKey if it's different from the discussionkey
                    MessageKey: ((messages[0].MessageKey === discussionKey) ? null : messages[0].MessageKey),
                    NumBefore: 1
                }
            )
            .done
            (
                function (result) 
                {
                    console.debug("chat.InsertMessages", "Conversation does not exist yet in our view, so insert it.", discussionKey, messages, result);
                    chat.InsertConversation(result);
                    chat.InsertMessages(discussionKey, messages, temporaryKey, snippet, shiftToTop, markUnread);
                }
            );

            return; // we call chat.InsertMessages again after the conversation has been inserted
        }

        // Start the process of monitoring for viewport resizing, so we can maintain
        // the user's current position in our message list and not bounce them all over.
        chat.StartResizing();

        var latestMessageDate = $("#dn-chat-conversation-" + discussionKey).data("latest") || dn.constants.DateMaxValue;

        messages.forEach
        (
            function (message)
            {
                if (!message.DateCompleted)
                {
                    // Don't display drafts in-line.
                    return;
                }

                var sequence = parseInt(message.Sequence);
                if (chat.Conversations[message.DiscussionKey] && sequence < chat.Conversations[message.DiscussionKey].EarliestSequence)
                {
                    chat.Conversations[message.DiscussionKey].EarliestSequence = sequence;
                }

                var chatClass = (dn.user.UserKey === message.Author.UserKey) ? "dn-chat-message-mine" : "dn-chat-message-other"

                var files = '';

                if (message.DateCompleted > latestMessageDate)
                {
                    latestMessageDate = message.DateCompleted;
                }

                if (message.FileKeys.length > 0)
                {
                    files = '<div class="reply-attachments col-12 pl-0 ml-0 mb-0 mt-2 form-row dn-attachments has-attachments" id="dn-attachments' + message.DiscussionKey + '">';

                    for (var i = 0; i < message.FileKeys.length; i++)
                    {
                        var fileKey = message.FileKeys[i];
                        var fileType = message.FileTypes[i];

                        if ((message.FileTypes[i] & dn.constants.FileTypes.Image) != 0) // image
                        {
                            // we use "display-" prefix in the `id` here so that we don't collide with thumbnails being shown in compose/reply/draft windows.
                            files += '<div id="display-' + fileKey + '" data-fancybox-trigger="gallery-' + message.MessageKey + '" data-src="/file/' + fileKey + '/lg" data-filetype="' + fileType + '" class="dn-file-wrapper mr-1 mb-1 float-left"><img src="/file/' + fileKey + '/sm" class="img-fluid mh-100 rounded" /></div>';
                        }
                        else // something other than image (use /embed)
                        {
                            // we use "display-" prefix in the `id` here so that we don't collide with thumbnails being shown in compose/reply/draft windows.
                            files += '<div id="display-' + fileKey + '" data-fancybox-trigger="gallery-' + message.MessageKey + '" data-type="iframe" data-src="/file/' + fileKey + '/embed" data-filetype="' + fileType + '" class="dn-file-wrapper mr-1 mb-1 float-left ' + (message.FileTypes[i] == dn.constants.FileTypes.Video ? "video-thumbnail-overlay" : "") + '"><img src="/file/' + fileKey + '/sm" class="img-fluid mh-100 rounded" /></div>';
                        }                                      
                    }

                    files += '</div>';
                }

                // Insert the reply after the parent message it is replying to.
                var dateLastEdited = message.DateCompleted ? ((new Date(message.DateCompleted)).getTime() / 1000) : 0;

                var $existingReply;

                // Look for existing reply.
                if (temporaryKey)
                {
                    $existingReply = $("#dn-chat-conversation-messages-" + discussionKey).find('div[data-messagekey=' + temporaryKey + '], div[data-messagekey=' + message.MessageKey + ']').not('.dn-typing-indicator');

                    if ($existingReply.length > 1)
                    {
                        // If we found more than one, it's because we have a temporaryKey and a MessageKey, remove the temporaryKey one.
                        console.debug("chat.InsertMessages", "Removing duplicate temporaryKey message " + temporaryKey);
                        $existingReply.filter('div[data-messagekey=' + temporaryKey + ']').not('.dn-typing-indicator').remove();
                    }
                }
                else
                {
                    $existingReply = $("#dn-chat-conversation-messages-" + discussionKey).find('div[data-messagekey=' + message.MessageKey + ']').not('.dn-typing-indicator');
                }

                // Eliminate any typing indicators for this user in this conversation
                $('#dn-typing-indicator-' + discussionKey + '-' + message.Author.UserKey).remove();

                // Is this message a system message?
                if (message.Author.UserKey == dn.constants.CommunitySuperUserKey)
                {
                    chatClass += " dn-system-message"
                }                

                // Create the new reply.
                var $newReply = 
                $(
                    '<div class="row dn-reply-wrapper ' + chatClass // + (chat.MutedConversations[conversation.DiscussionKey] ? " muted-avatar-container " : "") 
                        + '" id="dn-message-' +  message.MessageKey 
                        + '" data-messagekey="' + message.MessageKey 
                        + '" data-userkey="' + message.Author.UserKey 
                        + '" data-authorname="' + dn.text.QuoteAttribute(message.Author.FirstName + ' ' + message.Author.LastName) 
                        + '" data-lastedited="' + dateLastEdited 
                        + '" data-sequence="' + message.Sequence + '" tabindex="-1">\
                        <div class="dn-chat-bubble">\
                            <div class="dn-chat-avatar avatar-' + message.Author.UserKey  + '">\
                                <a href="/u/' + message.Author.UserKey + '">\
                                <img alt="" src="' + message.Author.UrlProfilePhoto + '" />\
                                </a>' +
                                // + (isMuchat.MutedConversations[conversation.DiscussionKey]ted ? dn.GetMuteBellIconDiv() : "")
                            '</div>\
                            <div class="media-body dn-reply" tabindex="-1">' +
                                (message.BodyWithEmbeds || message.Body) +
                                files +
                            '</div>\
                        </div>\
                        <div class="dn-chat-timestamp">\
                            <small class="opacity-60">\
                                <span class="datetime-utc dn-datecompleted" data-locale="en-short" data-timestamp="' + ((new Date(message.DateCompleted)).getTime() / 1000) + '">&nbsp;</span>\
                            </small>\
                        </div>\
                    </div>'
                );

                if ($existingReply.length)
                {
                    // Already exists, likely an edited message, or for some reason it got loaded twice.
                    // Overwrite what's already there if it's been edited since it was last viewed.
                    console.debug("chat.InsertMessages", "Replacing Message", $existingReply);
                    $existingReply.replaceWith($newReply);
                }
                else
                {
                    // Compute where to insert the reply. It's pretty easy, we try to find the
                    // message with the sequence AFTER ours, and if we find it, insert this 
                    // message BEFORE it. If not found, we look for the sequence BEFORE ours, 
                    // we insert this message AFTER it. If still not found, we append to the end.
                    var inserted = false;

                    if (sequence > -1)   // Sequence could be null or falsy if we're inserting the reply BEFORE it's saved
                    {
                        var sequenceAfter = sequence + 1;
                        var $after = $("#dn-chat-conversation-messages-" + discussionKey).find('div[data-sequence=' + sequenceAfter + ']');

                        if ($after.length)
                        {
                            // Insert this message before this.
                            console.debug("chat.InsertMessages", "Inserting Before", $after);
                            $after.before($newReply);
                            inserted = true;
                        }
                        else
                        {
                            var sequenceBefore = sequence - 1;
                            var $before = $("#dn-chat-conversation-messages-" + discussionKey).find('div[data-sequence=' + sequenceBefore + ']');

                            if ($before.length)
                            {
                                // Insert this after before this.
                                console.debug("chat.InsertMessages", "Inserting After", $before);
                                $before.after($newReply);
                                inserted = true;
                            }
                        }
                    }

                    if (!inserted)
                    {
                        // Insert this reply at the very end.
                        console.debug("chat.InsertMessages", "Inserting at End", $after);
                        $('#dn-chat-conversation-bottom-' + discussionKey).before($newReply);
                    }
                }
            }
        );

        // Update the .dn-chat-timestamp element with the latest date completed.
        if (latestMessageDate !== dn.constants.DateMaxValue)
        {
            $("#dn-chat-conversation-" + discussionKey).find("div.dn-chat-timestamp").html(dn.FormatDateHtml(moment(latestMessageDate).locale('en-short'), 'Latest: '));

            // Update the conversation timestamp
            $("#dn-chat-conversation-" + discussionKey).data("latest", ("" + latestMessageDate));
        }
        
        // Traverse unprocessed chat replies in the UI, and try to improve the UI by grouping
        // messages together if they are made by the same author. The UI will likely reflect
        // this by reducing the padding between chat bubbles, only displaying the avatar photo
        // and chat bubble "pointer" on the last message in a group.
        // 
        // Iterate over the existing, unprocessed chat messages, in reverse, so that
        // we can show the avatar, chat bubble arrow, and extra spacing on the last 
        // message from a user, in a row.

        var lastUserKey = null;

        // TODO: It would be best if we could refactor this to only iterate over the
        // newly-added messages, but the key is you may need to also reformat nearby
        // messages. For example, if the same author is posting a new message, their
        // previous message would have the dn-chat-last-message class removed, and 
        // then added to the new message. Or if we scroll up and load older messages 
        // in the discussion, we may need to reformat nearest neighbors (i.e. the 
        // _current_ oldest message prior to older messages being loaded).

        $($("#dn-chat-conversation-messages-" + discussionKey + " div.dn-reply-wrapper").get().reverse())
        .each
        (
            function() 
            {
                var userKey = $(this).data('userkey');

                if (userKey != lastUserKey)
                {
                    console.debug($(this), userKey + " != " + lastUserKey)
                    $(this).addClass("dn-chat-last-message");
                }
                else
                {
                    console.debug($(this), userKey + " == " + lastUserKey)
                    $(this).removeClass("dn-chat-last-message");
                }

                lastUserKey = userKey;
            }
        );

        dn.message.AddEventHandlers();
        dn.message.UnfurlLinks($('#dn-chat-conversation-messages-' + discussionKey));
        dn.ConvertTimestamps();

        if (shiftToTop)
        {
            dn.chat.ShiftConversationToTop(discussionKey);
        }

        if (markUnread)
        {
            dn.chat.MarkConversationUnread(discussionKey, ($("#dn-chat-conversation-" + discussionKey).data("unread") || 0) + messages.length);
        }

        if (snippet)
        {
            $('#dn-chat-conversation-' + discussionKey + ' .dn-chat-snippet').html(snippet);
        }
    };

    /**
     * Returns true if we're on the chat view, false otherwise.
     */
    chat.IsChatView = function()
    {
        return ($("#dn-chat-list-conversations").length > 0);
    };

    /**
     * Load more conversations in the side view panel.
     * 
     * @param {string}  discussionKey   - (Optional) If given, we'll highlight this conversation.
     * @param {string}  keyword         - (Optional) If given, we'll only show conversations matching these keywords.
     */
    chat.LoadConversations = function(discussionKey, keywords)
    {
        console.debug("chat.LoadConversations", discussionKey, keywords);

        if (dn.chat.Type.ViewOptions.HideList)
        {
            // If we're hiding the list, don't load conversations.
            return false;
        }

        chat.Keywords = (keywords || null);

        $.postJSON
        (
            "/api/v1/messages", 
            {
                PageStartIndex: dn.chat.CurrentPageChatList * dn.constants.MaxMessagesPerPage,
                PageItemCount: dn.constants.MaxMessagesPerPage,
                Keywords: chat.Keywords,
                Types: [dn.chat.Type.TypeId],
                NoReplies: (chat.Keywords == null),
                Sort: (chat.Keywords == null ? 6 : 0)
            }
        )
        .done
        (
            function (conversations) 
            {
                chat.TotalAvailableConversations = conversations.NumTotal;
                dn.chat.CurrentPageChatList++;
                
                if (conversations && conversations.Messages.length > 0) 
                {
                    for (var i = 0; i < conversations.Messages.length; i++)
                    {
                        chat.InsertConversation(conversations.Messages[i]);
                    }
                }
                else
                {
                    console.debug('End of conversations reached, will NOT load more for keywords: ' + chat.Keywords);
                    chat.HasMoreConversations[chat.Keywords] = false;
                }

                var numMuted = Object.keys(chat.MutedConversations).length;

                if (numMuted > 0)
                {
                    var msg = numMuted + ' Muted Conversation' + (numMuted > 1 ? 's' : '') + ' <span id="dn-show-muted-conversation" class="dn-link">(show)</span>' 
                    $("#dn-chat-num-muted").html(msg)
                    $("#dn-chat-list-footer").show()

                    //Setup the "show/hide" button for muted conversations
                    $("#dn-show-muted-conversation").click
                    (
                        function(e)
                        {
                            if ($("#dn-show-muted-conversation").html() == "(show)")
                            {
                                $(".dn-chat-list-conversation.dn-muted-conversation").removeClass("d-none").addClass("d-flex")
                                $("#dn-show-muted-conversation").html("(hide)")
                            }
                            else
                            {
                                $(".dn-chat-list-conversation.dn-muted-conversation").removeClass("d-flex").addClass("d-none")
                                $("#dn-show-muted-conversation").html("(show)")
                            }
                        }
                    )
                }                

                if (discussionKey)
                {
                    chat.HighlightConversation(discussionKey);
                }

                chat.HighlightUnreadConversations(conversations.Metadata);

                // Setup the conversation list scrolling including top/bottom markers used to 
                // determine when we need to load more 
                var chatMonitor = chat.Conversations.ChatListMonitor = scrollMonitor.createContainer($(".dn-chat-list")[0]);

                if ($(".dn-chat-list-conversation").is(":visible")) // only do this if the conversation list is not hidden (e.g. due to ViewOptions.HideList being true)
                {
                    chatMonitor.BottomViewMonitor = chatMonitor.create($(".dn-chat-list-conversation").last(), 200);
                    // chatMonitor.BottomViewMonitor.enterViewport(dn.chat.ReachedBottomConversationList());
                    chatMonitor.BottomViewMonitor.enterViewport(function() { 
    
                        // If we haven't loaded all the messages yet, lets attempt to load more 
                        if (chat.HasMoreConversations[chat.Keywords])
                        {
                            // Note - page incrementing already existed in the below function so we aren't going
                            // to do anything with it here...
                            chat.LoadConversations();
                        }  
                    });
                }
            }
        )
        .fail
        (
            function (xhr, textStatus, errorThrown) 
            {
                if (!dn.IsErrorHandledAutomatically(null, xhr, null, errorThrown))
                {
                    console.error("LoadConversations Failure", xhr, textStatus, errorThrown);
                    dn.ShowModal({ title: "Error Loading Conversations", body: dn.FormatApiError(xhr.responseJSON) });
                }
            }
        );
    };

    // Loads the rich text editor into the page, then call the callback with the result (the editor)
    function loadRichTextEditor(discussionKey, callbackFunction)
    {
        dn.LoadRichTextEditor
        (
            function()
            {
                console.debug("chat.LoadMessages", "dn.LoadRichTextEditor", "onComplete Executed");

                // set up CKEditor
                BalloonEditor.create
                (
                    $("#dn-chat-reply-body-" + discussionKey)[0], 
                    // Merge in our custom placeholder for this CKEditor instance
                    Object.assign
                    (
                        {
                            placeholder: $("#dn-chat-reply-body-" + discussionKey).attr("placeholder")
                        }, 
                        dn.constants.HtmlEditorConfig
                    )
                )
                .then
                (   
                    function(editor)
                    {
                        callbackFunction(editor)
                    }                    
                )
                .catch
                (
                    function (error) 
                    {
                        console.error(error);
                    }
                );
            }
        );
    }

    /**
     * Load the given discussion, or if already loaded, load more replies.
     */
    chat.LoadMessages = function(discussionKey, options, onSuccess, onError)
    {
        // Remove the pending indicators if they were added
        chat.MarkConversationRead(discussionKey);

        // Record currently visible conversation's scroll top/bottom/height for later use.
        if (chat.$CurrentConversation != null)
        {
            var previousDiscussionKey = chat.$CurrentConversation.data("discussionkey");
            chat.Conversations[previousDiscussionKey].ScrollBottom = chat.Conversations[previousDiscussionKey].ScrollHeight - chat.Conversations[previousDiscussionKey].ScrollTop;
            chat.Conversations[previousDiscussionKey].ScrollTop = chat.$CurrentConversation.scrollTop();
            chat.Conversations[previousDiscussionKey].ScrollHeight = chat.$CurrentConversation[0].scrollHeight;
            chat.Conversations[previousDiscussionKey].PreviousHeight = chat.$CurrentConversation[0].firstElementChild.getBoundingClientRect().height;

            console.debug("chat.LoadMessages", "Saving previous conversation scroll bottom/top/height and bounding height for " + previousDiscussionKey + ".", chat.Conversations[previousDiscussionKey].ScrollBottom, chat.Conversations[previousDiscussionKey].ScrollTop, chat.Conversations[previousDiscussionKey].ScrollHeight, chat.Conversations[previousDiscussionKey].PreviousHeight);
        }

        // Then hide all conversations except this one
        $(".dn-chat-conversation-messages:not(#dn-chat-conversation-messages-" + discussionKey + ")").hide();

        // Then un-highlight all other conversations in the left pane, and make
        // sure the one associated with discussionKey is highlighted.
        chat.HighlightConversation(discussionKey);

        // Assume this isn't the first time we've loaded this conversation, unless proven otherwise.
        var firstLoad = false;

        // Keep track of what our currently-viewed conversation is.
        chat.$CurrentConversation = $("#dn-chat-conversation-messages-" + discussionKey);
        chat.DiscussionKey = discussionKey;

        if (!chat.$CurrentConversation.length)
        {
            // This discussion has not been loaded yet, do some initial setup.
            firstLoad = true;

            if (!chat.Conversations[discussionKey])
            {
                chat.Conversations[discussionKey] = 
                {
                    // Defaults
                    LastLoadBottom: 0,
                    LastLoadTop: 0,
                    PreviousHeight: 0
                };
            }

            // Hide existing reply forms before we insert this new one
            $("#dn-chat-reply-form .dn-chat-reply").hide();

            // Create a CKEditor instance for this conversation for replying.
            if (dn.constants.UseRichTextEditor) 
            {
                // Insert the div that will hold the rich text editor
                $("#dn-chat-reply-form").prepend
                (
                    '<div class="dn-chat-reply" id="dn-chat-reply-' + discussionKey + '" data-discussionkey="' + discussionKey + '">\
                        <div class="dn-reply-attachments" id="dn-reply-attachments-' + discussionKey + '"></div>\
                        <div class="dn-reply-form-wrapper" >\
                            <div class="dn-chat-reply-body bg-white col-10 col-sm-11" id="dn-chat-reply-body-' + discussionKey + '" data-discussionkey="' + discussionKey + '" placeholder="' + chat.Placeholder + '" tabindex="0"></div>\
                            <div class="buttons ml-2 mr-0 w-100">\
                                <button type="button" class="btn btn-primary d-inline-block w-100 dn-send-reply" tabindex="0" id="dn-send-reply' + discussionKey + '">\
                                    <i class="fas fa-arrow-up"></i>\
                                </button>\
                                <button type="button" class="btn dn-button dn-compose-upload w-100 mt-1" tabindex="0" id="dn-compose-upload-' + discussionKey + '">\
                                    <i class="fal fa-paperclip"></i>\
                                </button>\
                            </div>\
                        </div>\
                    </div>'
                );

                loadRichTextEditor
                (
                    discussionKey, 
                    function (editor) 
                    {
                        // initiate our reply
                        chat.SetupForm(editor, discussionKey);
                    }
                );
            } 
            else 
            {
                // insert the plaintext editor for this discussion
                $("#dn-chat-reply-form").prepend(
                    '<div class="dn-chat-reply" id="dn-chat-reply-' + discussionKey + '" data-discussionkey="' + discussionKey + '">\
                        <div class="dn-reply-attachments" id="dn-reply-attachments-' + discussionKey + '"></div>\
                        <div class="dn-reply-form-wrapper" >\
                            <textarea class="dn-chat-reply-body bg-white d-block ' + dn.constants.Classnames.TextOnlyEditor + '" style="padding: 10px; display: inline-block; width: 100%;" tabindex="0" placeholder="' + chat.Placeholder + '" id="dn-chat-reply-body-' + discussionKey + '" />\
                            <div class="buttons ml-2 mr-0 w-100">\
                                <button type="button" class="btn btn-primary d-inline-block w-100 dn-send-reply" tabindex="0" id="dn-send-reply' + discussionKey + '">\
                                    <i class="fas fa-arrow-up"></i>\
                                </button>\
                                <button type="button" class="btn dn-button dn-compose-upload w-100 mt-1" tabindex="0" id="dn-compose-upload-' + discussionKey + '">\
                                    <i class="fal fa-paperclip"></i>\
                                </button>\
                            </div>\
                        </div>\
                    </div>');

                // initiate our reply
                chat.SetupForm($("#dn-chat-reply-body-" + discussionKey).first(), discussionKey);
            }

            // Bind the attachment controller to the button
            setupAttachmentControls
            (
                $("#dn-compose-upload-" + discussionKey), 
                discussionKey, 
                $("#dn-reply-attachments-" + discussionKey)
            );

            // Create the new conversation div
            chat.$CurrentConversation = 
            $(
                '<div id="dn-chat-conversation-messages-' + discussionKey + '" class="dn-chat-conversation-messages" data-discussionkey="' + discussionKey + '">\
                    <div class="d-block">\
                        <div id="dn-chat-conversation-top-' + discussionKey + '" tabindex="-1" class="dn-chat-conversation-top"></div>\
                        <div id="dn-chat-conversation-bottom-' + discussionKey + '" tabindex="-1" class="dn-chat-conversation-bottom"></div>\
                    </div>\
                </div>'
            );

            // Setup the containers for the conversation, including top/bottom markers used to 
            // determine when we need to load more replies.
            $("#dn-chat-conversation-messages").after(chat.$CurrentConversation);

            chat.Conversations[discussionKey].PreviousHeight = chat.$CurrentConversation[0].firstElementChild.getBoundingClientRect().height;

            // This is an instance of the scroll monitor that listens to scroll events on this container.
            chat.Conversations[discussionKey].ContainerMonitor = scrollMonitor.createContainer($("#dn-chat-conversation-messages-" + discussionKey));

            // Setup our listener for when we move near the bottom
            chat.Conversations[discussionKey].BottomViewMonitor = chat.Conversations[discussionKey].ContainerMonitor.create($("#dn-chat-conversation-bottom-" + discussionKey), 20);
            chat.Conversations[discussionKey].BottomViewMonitor.enterViewport(chat.ReachedBottom);

            // Setup our listener for when we move near the top
            chat.Conversations[discussionKey].TopViewMonitor = chat.Conversations[discussionKey].ContainerMonitor.create($("#dn-chat-conversation-top-" + discussionKey), 20);
            chat.Conversations[discussionKey].TopViewMonitor.enterViewport(chat.ReachedTop);

            // Initiate the sequence tracking so we know when we need to load more messages.
            chat.Conversations[discussionKey].EarliestSequence = 4294967295; // Safe upper bound: Math.pow(2, 32) - 1.
        }
        else
        {
            // The conversation was previously loaded, so just show it now.
            chat.$CurrentConversation.show();

            // Hide existing reply forms from other conversations
            $("#dn-chat-reply-form .dn-chat-reply").hide();

            // Un-hide the newly selected conversation reply form
            $("#dn-chat-reply-" + discussionKey).show();

            // So far I've found that this code is not needed, but I'm keeping this here until we get more testing
            // console.debug("Scrolling to previous position: " + chat.Conversations[discussionKey].ScrollTop);
            // chat.$CurrentConversation.scrollTop(chat.Conversations[discussionKey].ScrollTop);
        }

        // Load (more) messages
        $.postJSON
        (
            "/api/v1/chat/" + discussionKey, 
            options
        )
        .done
        (
            function (result) 
            {
                // Only bother putting this discussion into the UI if the root message was completed.
                if (dn.chat.DiscussionKey && $("#dn-chat-conversation-" + discussionKey).length === 0)
                {
                    // Our current conversation is not present - it may not have been indexed yet and thats why it wasn't loaded.
                    console.debug("chat.LoadMessages", "Current conversation not present, inserting it now.", dn.chat.DiscussionKey, discussionKey, result);
                    chat.InsertConversation(result);
                }

                // Determine if archived and disable sending functions if so, also disable the
                // ability to leave
                if (result.Options == 1)//Readonly
                {
                    $("#dn-chat-leave-conversation").hide();
                    $("#dn-chat-reply-form button").prop('disabled', true);

                    if (dn.constants.UseRichTextEditor)
                    {
                        chat.Editors[discussionKey].enableReadOnlyMode(discussionKey)
                    }
                    else
                    {
                        $("#dn-chat-reply-body-" + discussionKey).attr("disabled", "disabled"); 
                    }
                }
                else
                {
                    // Determine leave option (delete always allowed)
                    var participantsCount = $("#dn-chat-recipients-" + discussionKey).data("participants-count");

                    if (participantsCount > 2)
                        $("#dn-chat-leave-conversation").show();
                    else
                        $("#dn-chat-leave-conversation").hide();

                    if (!chat.IsReadOnly)
                    {
                        $("#dn-chat-entry").show();
                    }

                    if (dn.constants.UseRichTextEditor)
                    {
                        chat.Editors[discussionKey].disableReadOnlyMode(discussionKey)
                    }
                    else
                    {
                        $("#dn-chat-reply-body-" + discussionKey).removeAttr("disabled"); 
                    }


                    $("#dn-chat-reply-form button").prop('disabled', false);
                }

                chat.InsertMessages(discussionKey, result.Messages);

                chat.ApplyAuthorIcons(result.ActiveAuthors);

                if (firstLoad)
                {
                    // First time loading messages into this conversation view, we're going to scroll to
                    // either the provided MessageKey, or if not provided, the bottom of the conversation.
                    setTimeout
                    (
                        function() 
                        {
                            if (options.MessageKey)
                            {
                                // Scroll to the provided reply MessageKey
                                console.debug("chat.LoadMessages", "Scroll to MessageKey " + options.MessageKey + " in the conversation.");
                                $("#dn-message-" + options.MessageKey).focus();
                            }
                            else
                            {
                                // Loading a conversation with no specific reply highlighted, so 
                                // just scroll to the bottom of the message list when we first load.
                                chat.ScrollToBottom();

                                // Now put the focus in the reply box.
                                $("#dn-chat-reply-body-" + discussionKey).focus();
                            }
                        }, 
                        750
                    );
                }

                //chat.GetDraftForDiscussion(discussionKey);
                getRandomTempKey(discussionKey);

                var fileKeys = chat.Editors[discussionKey].FileKeys;
                var fileTypes = chat.Editors[discussionKey].FileTypes;

                if (fileKeys === undefined)
                {
                    fileKeys = chat.Editors[discussionKey].FileKeys = []
                    fileTypes = chat.Editors[discussionKey].FileTypes = []
                }

                if (onSuccess)
                {
                    onSuccess(result);
                }
            }
        )
        .fail
        (
            function (xhr, textStatus, errorThrown) 
            {
                if (onError)
                {
                    onError(errorThrown);
                }
                else
                {
                    dn.ShowModal({ title: "Error Loading Conversation", body: xhr.responseJSON });
                    console.error(xhr, textStatus, errorThrown);
                    $(".dn-chat-conversation-messages").hide()
                    $('.dn-chat-show-if-conversation').hide();
                    $('.dn-chat-hide-if-conversation').show();
                }
            }
        );


        $('.dn-chat-show-if-conversation').show();
        $('.dn-chat-hide-if-conversation').hide();

        // Incase we hid it earlier 
        $(".dropdown").show()
    };

    /**
     * Mark the given conversation as read (hide the unread indicator, etc.)
     */
    chat.MarkConversationRead = function(discussionKey) 
    {
        var numUnread = $("#dn-chat-conversation-" + discussionKey).data("unread");

        if (numUnread > 0)
        {
            dn.UnreadChatRepliesUpdate(-numUnread);
        }

        $("#dn-chat-conversation-" + discussionKey + " .dn-chat-snippet").removeClass("dn-chat-unread");
        $("#dn-chat-conversation-" + discussionKey + " .dn-chat-unread-indicator").hide();
    };

    /**
     * Mark the given conversation as unread (show the unread indicator, etc.), unless
     * the user is currently viewing that discussion.
     */
    chat.MarkConversationUnread = function(discussionKey, numUnread) 
    {
        if (chat.DiscussionKey != discussionKey)
        {
            $("#dn-chat-conversation-" + discussionKey).data("unread", numUnread);

            $("#dn-chat-conversation-" + discussionKey + " .dn-chat-snippet").addClass("dn-chat-unread");
            $("#dn-chat-conversation-" + discussionKey + " .dn-chat-unread-indicator").show();
        }
    };

    /**
     * We've reached the bottom, so we're going to anchor here and see if we need to load more messages.
     */
    chat.ReachedBottom = dn.Debounce
    (
        function(event)
        {
            var discussionKey = chat.$CurrentConversation.data("discussionkey");
            var timestamp = (new Date()).getTime();

            // Only bother loading new messages if we did it more than X seconds ago
            if (chat.Conversations[discussionKey].LastLoadBottom < (timestamp - chat.MillisecondsUntilLoadMore))
            {
                var $lastReply = $("#dn-chat-conversation-messages-" + discussionKey + " div.dn-reply-wrapper").first();

                console.debug("chat.ReachedBottom", discussionKey, event);

                // Try to load more replies after the current bottom messagekey.
                var lastMessageKey = $lastReply.data('messagekey');

                if (lastMessageKey)
                {
                    // Set the anchor to null, since when we're loading more at the bottom of the viewport, 
                    // and it shouldn't cause the viewport loading to move around as happens when at the top.
                    chat.Conversations[discussionKey].LastLoadBottom = timestamp;

                    chat.LoadMessages
                    (
                        discussionKey, 
                        {
                            MessageKey: lastMessageKey,
                            NumAfter: chat.NumRepliesPerPage
                        }
                    );
                }
            }
        }, 
        1000, true // execute immediately, but not again for at least 1000 ms;
    );

    /**
     * We've reached the top, so we're going to see if we need to load more messages.
     */
    chat.ReachedTop = dn.Debounce
    (
        function(event)
        {
            var discussionKey = chat.$CurrentConversation.data("discussionkey");
            var timestamp = (new Date()).getTime();

            // Only bother loading new messages if we did it more than X seconds ago
            if (chat.Conversations[discussionKey].LastLoadTop < (timestamp - chat.MillisecondsUntilLoadMore))
            {
                var $firstReply = $("#dn-chat-conversation-messages-" + discussionKey + " div.dn-reply-wrapper").first();
                var firstSequence = $firstReply.data('sequence');

                console.debug("chat.ReachedTop", discussionKey, event, firstSequence);

                if (firstSequence)
                {
                    // Try to load more replies before the current top messagekey.
                    var firstMessageKey = $firstReply.data('messagekey');

                    if (firstMessageKey)
                    {
                        chat.Conversations[discussionKey].LastLoadTop = timestamp;

                        chat.LoadMessages
                        (
                            discussionKey, 
                            {
                                MessageKey: firstMessageKey,
                                NumBefore: chat.NumRepliesPerPage
                            }
                        );
                    }
                }
            }
        }, 
        1000, true // execute immediately, but not again for at least 1000 ms;
    );

    /**
     * The function for the click event
     * @param {*} fileKey the fileKey of the file to remove
     * @param {string} targetContainerSelector - The container's selector where the attachments are located
     */
    removeFile = function(fileKey, targetContainerSelector) 
    {
        // remove it from the UI
        $('#' + fileKey).remove();

        // Maintain the list locally for the purposes of sending the message
        // NOTE: 'Editors' not 'Conversation'
        var discussionKey = dn.chat.DiscussionKey || "dn-chat-start-conversation-body";

        // FileKeys is a list, but this is accessing it as a dict
        if (chat.Editors[discussionKey] && chat.Editors[discussionKey].FileKeys && chat.Editors[discussionKey].FileKeys.length > 0)
        {
            // Loop backwards so we can remove any matching files from the array without adjusting anything
            var i = chat.Editors[discussionKey].FileKeys.length;

            while (i--) 
            {
                if (chat.Editors[discussionKey].FileKeys[i] == fileKey) 
                { 
                    chat.Editors[discussionKey].FileKeys.splice(i, 1);
                    chat.Editors[discussionKey].FileTypes.splice(i, 1);
                }
            }

            if (chat.Editors[discussionKey].FileKeys.length === 0)
            {
                $(targetContainerSelector).removeClass("has-attachments");
            }            
        }
    }

    /**
     * Remove the chat from the client
     */
    chat.RemoveFromClient = function(discussionKey)
    {
        // Remove it from the side panel
        $("#dn-chat-conversation-" + discussionKey).remove();

        // Update the UI to go back to the empty view
        $(".dn-chat-conversation-messages").hide();
        $('.dn-chat-show-if-conversation').hide();
        $('.dn-chat-hide-if-conversation').show();

        // remove it from the view's data collection
        chat.CurrentPageChatList--;
        chat.$CurrentConversation = null;
        chat.DiscussionKey = null;
        delete chat.Conversations[discussionKey];

        history.replaceState(null, document.title, dn.chat.Type.Link);
    };

    /**
     * Saves the contents of the editor to local storage so that it can be retrieved later 
     * @param {string} discussionKey 
     */
    chat.SaveUpdateDraft = function(discussionKey)
    {
        console.debug("chat.SaveUpdateDraft", "Saving draft for " + discussionKey);
        //var content = (chat.Editors[discussionKey].getData() || "").replace(dn.constants.RegEx.EmptyParagraphs, '');
        //dn.storage.StoreLocal(dn.user.UserKey, chat.Namespace + discussionKey, content)
    };

    /**
     * Sets up a passed button to accept attachments, and put them into the target container
     * using the discussion key as the unique identifier.
     * 
     * @param {string} $attachmentButton - The reference to the attachment button
     * @param {string} discussionKey - The key for the editor to use to store the values in/on
     * @param {string} targetContainerSelector - The container where to put the attachments
     */
    setupAttachmentControls = function($attachmentButton, discussionKey, targetContainerSelector)
    {
        dn.compose.InitFileUploader(); // So that the attach button will work.

        // using document as they are dynamically created
        $($attachmentButton).click
        (
            function() 
            {
                if (!dn.user.CanUpload)
                {
                    dn.PromptToUpgrade("CanUpload");
                    return;
                }
                else
                {
                    var fileKeys = chat.Editors[discussionKey].FileKeys;
                    var fileTypes = chat.Editors[discussionKey].FileTypes;

                    if (fileKeys === undefined)
                    {
                        fileKeys = chat.Editors[discussionKey].FileKeys = [];
                        fileTypes = chat.Editors[discussionKey].FileTypes = [];
                    }

                    dn.compose.FileUploader.pick(dn.compose.FileUploaderOptions).then(function(response) 
                    {
                        dn.compose.HandleFiles
                        (
                            response,
                            fileKeys, 
                            fileTypes,
                            // After handling save the changes to our draft
                            function()
                            {
                                embedAttachmentsToPendingMessage(discussionKey, targetContainerSelector);
                            }
                        );
                    });
                }
            }
        );
    }

    /**
     * Scroll to the bottom of the feed.
     */
    chat.ScrollToBottom = function() 
    {
        if (chat.$CurrentConversation)
        {
            console.debug("chat.ScrollToBottom", "Scroll to the bottom of the conversation.");

            chat.$CurrentConversation.animate
            (
                {
                    scrollTop: chat.$CurrentConversation[0].scrollHeight
                }, 
                250
            );    
        }
    };

    /**
     * This sends the current reply text, either to save the draft, or to complete the
     * reply. If completed, the server will generate a new reply stub and send us that 
     * data in the response as well. 
     * 
     * @param {string}  discussionKey - The root messagekey of the conversation being replied to.
     */
    chat.SendReply = function(discussionKey)
    {
        var message = 
        {
            // Unlike when creating new chat conversations — where the user specifies all the
            // `Recipients` and we decide whether to add to an existing conversation or start
            // a new one — replies only specify the ParentKey, i.e. the original `DiscussionKey`.
            // This is so that we don't enter a race condition where, e.g. a participant drops
            // themselves from a conversation just as a reply is being sent in that targets them.
            // If the reply author truly wants to include that person, they can start a new 
            // conversation or add the participant back to the conversation first.
            FileKeys: (chat.Editors[discussionKey].FileKeys || []),
            FileTypes: (chat.Editors[discussionKey].FileTypes || []),
            DiscussionKey: discussionKey, // this is important to pass for chat types that are robot-powered, since it lets us have multiple convos with same participants
            ParentKey: discussionKey,
            TypeId: chat.Type.TypeId,
            Completed: true // this forces the reply to be finished/completed, if it started as a draft
        };

        if (dn.constants.UseRichTextEditor)
        {
            // Rich text editing, so we set the disable flag OFF, and
            // we grab the message data from the CKEditor getData() call.
            message.Body = (chat.Editors[discussionKey].getData() || "").replace(dn.constants.RegEx.EmptyParagraphs, '');
            message.DisableRichTextEditing = false;
            message.Format = "Html";
        }
        else
        {
            // Plain text editing, so we set the disable flag ON, and
            // we grab the message data from the regular jQuery val().
            message.Body = (chat.Editors[discussionKey].val() || "").replace(dn.constants.RegEx.EmptyParagraphs, '');
            message.DisableRichTextEditing = true;
            message.Format = "Plain";
        }

        if (!message.Body && message.FileKeys.length === 0)
        {
            console.debug("chat.SendReply()", "Ignoring empty message submission.");
            return;
        }

        // Make sure we're connected to our realtime connection, now that we're taking an action here.
        // This way if the user was sitting on the page so long that the WebSocket timed out, when they
        // initiate an action like this, we'll be reconnected, so that new, live updates can happen.
        dn.realtime.Connect();

        // Temporary identifier that we use as an internet MessageKey, for the purpose
        // of inserting their message into the UI immediately. 
        var temporaryKey = chat.Editors[discussionKey].TemporaryKey;

        console.debug("chat.SendReply", message, temporaryKey);

        // Add some keys that `chat.InsertMessages` is expecting.
        message.MessageKey = temporaryKey;
        message.DiscussionKey = discussionKey;
        message.Author = dn.user;

        // Immediately insert the reply into the display, even before it's fully saved. 
        // show the reply instantly, even before it's saved
        chat.InsertMessages(message.DiscussionKey, [message], null, null, true); 

        // scroll to the bottom so we see it
        chat.ScrollToBottom();

        // Clear the typing field
        if (dn.constants.UseRichTextEditor) 
        {
            chat.Editors[discussionKey].setData("");
        } 
        else 
        {
            chat.Editors[discussionKey].val("");
        }

        // clear attachments keys and types
        chat.Editors[discussionKey].FileKeys = []
        chat.Editors[discussionKey].FileTypes = []
        $(".dn-chat-reply .dn-reply-attachments").html("");

        // disable the post button
        $(".dn-send-reply").prop('disabled', true); 
        chat.MessageSendsInProgress++;

        // scroll to the bottom before sending helps keep up
        chat.ScrollToBottom();

        $.postJSON
        (
            "/api/v1/chat", 
            message
        )
        .always
        (
            function() 
            {
                // enable the post button
                $(".dn-send-reply").prop('disabled', false); 
                chat.MessageSendsInProgress--;
            }
        )
        .done
        (
            function (result) 
            {
                console.debug("chat.SendReply", "Succeeded!", result);

                // insert the new reply into the display, passing in the temporaryKey we originally
                // used when inserting their message immediately into the display, so it can be replaced.
                chat.InsertMessages(result.Message.DiscussionKey, [result.Message], temporaryKey, "You: " + result.Message.Snippet, true);

                // Clear out the stored temp-message
                dn.storage.Clear(dn.user.UserKey, chat.Namespace + discussionKey)
            }
        )
        .fail
        (
            function (xhr, textStatus, errorThrown) 
            {
                // insert the old failed body back into the beginning of the box.
                if (dn.constants.UseRichTextEditor) 
                {
                    // HTML allows us to insert it, and if there was anything else existing, 
                    // it will be wrapped in <p> or <div>. 
                    chat.Editors[discussionKey].setData(message.Body + chat.Editors[discussionKey].getData());
                } 
                else 
                {
                    var currentBody = chat.Editors[discussionKey].val();

                    // We need to put back what they were currently typing.
                    if (currentBody)
                    {
                        chat.Editors[discussionKey].val(message.Body + "\n\n" + currentBody);
                    }
                    else
                    {
                        chat.Editors[discussionKey].val(message.Body);
                    }
                }

                if (!dn.IsErrorHandledAutomatically(null, xhr, null, errorThrown)) 
                {
                    // TODO: Handle errors better
                    console.error("chat.SendReply", "Send reply failure", xhr, errorThrown); 
                    dn.ShowModal({ title: "Error Sending Message", body: dn.FormatApiError(xhr.responseJSON) });
                }
            }
        );
    };

    /**
     * Setup the reply form.
     */
    chat.SetupForm = function(editor, discussionKey)
    {
        chat.Editors[discussionKey] = editor;

        var debouncedTyping = dn.Debounce
        (
            function(e) 
            {
                dn.chat.Typing(discussionKey, e);
            },
            dn.chat.WaitForTyping,
            true // trigger immediately, then wait for additional triggers
        );

        var debouncedSaveUpdateDraft = dn.Debounce
        (
            function(e)
            {
                dn.chat.SaveUpdateDraft(discussionKey);
            }, 
            1000, //delay of 1 second after they have stopped editing the text of the message.,
            false
        );

        $("#dn-chat-reply-body-" + discussionKey)
        .off("keydown paste cut input")  // Re: https://github.com/dennyferra/TypeWatch/issues/36#issuecomment-722637558
        .on
        (
            'keydown paste cut input',  // case insensitive, sees all keys (compared to keypress, case sensitive and ignores CTRL/SHIFT/ALT)
            function(e) 
            {
                // 'Enter' does new 'p' elements, Shift+Enter does a <br>, nothing else inserts new p elements
                if (e.which === 13 && !(e.shiftKey || e.ctrlKey || e.altKey || e.metaKey))   // ENTER, no special modifiers
                {
                    e.stopPropagation();// don't bubble up
                    e.preventDefault(); // Prevents from enter sending a new line and causing whitespace in the editor
                    console.debug("chat.SetupForm()/ENTER", e);
                    chat.SendReply(discussionKey);
                }                
                else
                {
                    debouncedTyping();
                    debouncedSaveUpdateDraft();
                }
            }
        );
    };

    /**
     * Shift the conversation with the specified discussion key to the top of the conversation list
     */
    chat.ShiftConversationToTop = function(discussionKey) 
    {
        console.debug("chat.ShiftConversationToTop", discussionKey);
        $("#dn-chat-conversation-" + discussionKey).prependTo("#dn-chat-list-conversations");
    };

    /**
     * Show a chat conversation, which could mean we're going to send the user to the PM view
     * for their discussion, or if they're already on the PM view, just load the conversation.
     * 
     * NOTE: This doesn't execute when the user is loading the PM view directly from a PM conversation.
     * In that scenario, you may want to adapt chat.HighlightConversation().
     */
    chat.ShowConversation = function(discussionKey, messageKey, onSuccess)
    {
        var url = dn.chat.Type.Link + '/' + discussionKey;

        if (messageKey !== discussionKey)
        {
            // This is for a specific reply, so add a querystring to our URL,
            // and make sure to pass this MessageKey to the LoadMessages call.
            url += "?m=" + messageKey;
        }

        if (!chat.IsChatView())
        {
            // They are NOT on the chat page for this type, so redirect
            console.debug("chat.ShowConversation", "Redirecting...", url);

            window.location.href = url;
            return;
        }

        // Make sure we're connected to our realtime connection, now that we're taking an action here.
        // This way if the user was sitting on the page so long that the WebSocket timed out, when they
        // initiate an action like this, we'll be reconnected, so that new, live updates can happen.
        dn.realtime.Connect();

        // If we were overriding the hiding of the list of conversations (something we
        // do on small devices when the user taps the chevron to view conversations), 
        // then this clears the override.
        $(".dn-chat-list").removeClass("shown");

        // Set the URL to match what they just clicked
        history.replaceState(null, document.title, url);

        var alreadyVisible = (chat.$CurrentConversation && chat.$CurrentConversation.data("discussionkey") === discussionKey);

        // If the panel was already visible, consider this click a request to scroll to bottom
        if (alreadyVisible)
        {
            console.debug("Conversation Click", "alreadyVisible", alreadyVisible);

            // TODO: Perhaps we should see if we're already near the bottom, and if so, 
            // instead scroll back up to their previous location? I.e. letting them toggle
            // between the bottom and their last scrolled-to position?
            chat.ScrollToBottom();
        }

        // Fill in the messages
        chat.LoadMessages
        (
            discussionKey,
            {
                // Only pass MessageKey if it's different from the discussionkey
                MessageKey: ((messageKey === discussionKey) ? null : (messageKey || null)),
                NumBefore: chat.CalculateMessageCountForViewport()
            },
            function()
            {
                // If the panel was already visible, consider this click a request to scroll to bottom.
                // We do this above the ajax request as well, so that it happens immediately. We do it
                // here as well in case the ajax request loads additional new messages.
                if (alreadyVisible)
                {
                    chat.ScrollToBottom();
                }

                if (onSuccess)
                {
                    onSuccess();
                }
            }
        );
    };

    /**
     * Temporarily show a typing bubble for the given user.
     * 
     * TODO: This needs to be refactored to handle a couple additional scenarios:
     * 
     * 1. If the discussion is not currently being viewed, the typing indicator should be
     *    shown in the conversation list, instead of the snippet text. And since the discussion
     *    is not currently being viewed, should we even bother doing any of the current code?
     * 
     * 2. If the discussion is not currently being viewed, and the user clicks over to view it,
     *    once we load the discussion to show the latest messages, we need to understand that 
     *    another participant was typing and THEN show the bubble.
     * 
     * @param {string}    discussionKey  - The unique identifier of the conversation.
     * @param {object}    author         - The ChatParticipant that is typing
     */
    chat.ShowUserTyping = function(discussionKey, author)
    {
        console.debug("chat.ShowUserTyping()", discussionKey, author);

        if (dn.user.UserKey === author.UserKey)
        {
            console.debug("chat.ShowUserTyping()", "Ignoring because it's the current user.");
            return;
        }

        var id = "dn-typing-indicator-" + discussionKey + "-" + author.UserKey;
        var timeoutId = null;
        var $typingBubble = $("#" + id);

        var delayMilliseconds = ((chat.Type.Robot && chat.Type.Robot.UserKey === author.UserKey) ? chat.MillisecondsUntilDoneTypingRobot : chat.MillisecondsUntilDoneTyping);
        var delaySeconds = (delayMilliseconds / 1000);

        // Look for existing typing bubble.
        if ($typingBubble.length)
        {
            // Already exists, just reset the timer for removing it
            timeoutId = $typingBubble.data("timeoutId");
            console.debug("chat.ShowUserTyping()", "Resetting timeout on typing indicator.", timeoutId, delaySeconds);
            clearTimeout(timeoutId);
        }
        else
        {
            // Create the typing/activity bubble
            $typingBubble = 
            $(
                '<div class="row dn-reply-wrapper dn-typing-indicator dn-chat-last-message dn-chat-message-other" id="dn-typing-indicator-' + discussionKey + '-' + author.UserKey + '" data-messagekey="' + discussionKey + '" data-userkey="' + author.UserKey + '" data-authorname="' + dn.text.QuoteAttribute(author.FirstName + ' ' + author.LastName) + '">\
                    <div class="dn-chat-bubble">\
                        <div class="dn-chat-avatar">\
                            <img alt="" src="/u/' + author.UserKey + '/photo" />\
                        </div>\
                        <div class="media-body dn-reply" tabindex="-1"><span>···</span></div>\
                    </div>\
                    <div class="dn-chat-timestamp">\
                        <small class="opacity-60">&nbsp;</small>\
                    </div>\
                </div>'
            );

            console.debug("chat.ShowUserTyping()", "Inserting typing indicator and setting timeout to remove.", delaySeconds);

            // Remove the "dn-chat-last-message" temporarily from any chat bubble belonging to this user DIRECTLY above us (determined via prev())
            $("#dn-chat-conversation-bottom-" + discussionKey).before($typingBubble);

            $typingBubble.prev(".dn-chat-message-other.dn-chat-last-message[data-userkey=" + author.UserKey + "]").removeClass("dn-chat-last-message");

            // scroll to the bottom so we see it
            chat.ScrollToBottom();
        }

        // Whether we just appended a new chat bubble or found an existing one and cleared its timeout,
        // now we're going to establish a new timeout for clearing the bubble out. So if the user keeps
        // typing, the bubble should remain (until it's replaced with the actual message)
        timeoutId = setTimeout
        (
            function()
            {
                // Put the "dn-chat-last-message" class back on the previous message if it's written by this user
                $("#" + id).prev(".dn-chat-message-other[data-userkey=" + author.UserKey + "]").addClass("dn-chat-last-message");

                // Then remove the typing bubble
                $("#" + id).remove();

                // scroll to the bottom so we see it
                chat.ScrollToBottom();
            },
            delayMilliseconds
        );

        $typingBubble.data("timeoutId", timeoutId);
    };

    $("#dn-search-field-cancel").click
    (
        function()
        {
            $("#dn-search-field-entry").val("");
            $("#dn-search-field-entry").focus()

            $("#dn-search-field-container").hide()
            $("#dn-conversations-header").show()

            dn.chat.CurrentPageChatList = 0;
            dn.chat.LoadConversations(null, null);
        }
    );

    $(".dn-search-toggle").on
    (
        "click", function()
        {
            if ($("#dn-conversations-header").is(":visible"))
            {
                $("#dn-conversations-header").hide();
                $("#dn-search-field-container").show();
                $("#dn-search-field-entry").focus();
            }
            else
            {
                $("#dn-search-field-container").hide();
                $("#dn-conversations-header").show();
            }
        }
    );

    $("#dn-search-field-entry")
    .on
    (
        'keydown paste cut input',
        function(e) 
        {
            if (e.which === 13) 
            {
                $("#dn-chat-list-conversations").html("");
                dn.chat.CurrentPageChatList = 0;
                dn.chat.LoadConversations(null, $("#dn-search-field-entry").val());
            }                       
        }
    );




    // // Close button clicked
    // $('.dn-chat-start-conversation-close').click
    // (
    //     function(event) 
    //     {
    //         openModals.modal('show'); // re-show modals that were open when we first appeared

    //         if (opt.onClose)
    //         {
    //             opt.onClose();
    //         }
    //     }
    // );


    /**
     * Start a Private Message conversation with optional recipients.
     * 
     * @param {string}  opt.body            - The first message to send in the conversation.
     * @param {string}  opt.title           - The title/subject of their message to be pre-filled for them.
     * @param {array}   opt.prefilledUsers  - A list of UserKeys of recipients that will be preset in the form. If set, you must pass in `suggestUsers` as well, so they can rendered and auto-selected properly.
     * @param {array}   opt.suggestedUsers  - An array of initial user suggestions, if the user clicks into the user field, they can choose from these without typing. (REQUIRED if you are supplying `prefilledUsers`.)
     * @param {func}    opt.onSuccess       - If we're able to successfully invite (or queue invitation), we'll call this with the response.
     * @param {func}    opt.onError         - If we can't invite/approve the user for some reason, we'll call this with the jquery error info
     */
    chat.StartConversation = function(opt) 
    {
        if (!opt)
        {
            opt = {};
        }
        const editorBodyKey = "dn-chat-start-conversation-body";

        $('#dn-chat-start-conversation').remove();  // remove if it already exists
        $("body").append($('<div class="modal dn-chat-start-conversation" tabindex="-1" role="dialog" style="display: none" id="dn-chat-start-conversation">\
            <div class="modal-dialog" role="document">\
                <div class="modal-content">\
                    <div class="modal-header">\
                        <h5 class="modal-title"><b>' + dn.chat.Type.Name + '</b></h5>\
                        <button type="button" class="close dn-chat-start-conversation-close" data-dismiss="modal" aria-label="Close">\
                        <span aria-hidden="true">&times;</span>\
                        </button>\
                    </div>\
                    <div class="modal-body" style="background-color: #f8f9fa; padding-bottom: 0">\
                        <div class="dn-compose-form">' + 

                            // From:
                            '<div class="form-group col-12 px-0" id="dn-chat-from">\
                                <div class="input-group">\
                                    <div class="input-group-prepend">\
                                        <label class="input-group-text px-1" for="dn-chat-start-conversation-from">From</label>\
                                    </div>\
                                    <div class="form-control faux-selectize-input">\
                                        <div class="dn-user" id="dn-chat-start-conversation-from" data-value="' + dn.user.UserKey + '"><img class="dn-user-avatar" src="' + dn.user.UrlProfilePhoto + '">' + dn.user.FirstName + ' ' + dn.user.LastName + '</div>\
                                    </div>\
                                </div>\
                            </div>' +

                            // To: 
                            '<div class="form-group col-12 col-xl px-0" id="dn-chat-to">\
                                <div class="input-group dn-chat-start-conversation-user-search">\
                                    <div class="input-group-prepend">\
                                        <label class="input-group-text px-1" for="dn-chat-start-conversation-user-search">To</label>\
                                    </div>\
                                    <div class="recipient-field"></div>\
                                </div>\
                                <div class="invalid-feedback mx-1 mx-sm-0" id="dn-chat-start-conversation-user-search-error"></div>\
                            </div>' +

                            /* // Subject (for now we are not asking anyone for a subject/title for a PM)
                            '<div class="form-group col-12 px-0">\
                                <div class="input-group">\
                                    <div class="input-group-prepend">\
                                        <label class="input-group-text px-1" for="dn-chat-start-conversation-title">Subject</label>\
                                    </div>\
                                    <input class="form-control dn-chat-start-conversation-title" autocomplete="off" id="dn-chat-start-conversation-title" type="text" value="' + opt.title + '" placeholder="Topic of conversation">\
                                </div>\
                                <div class="invalid-feedback mx-1 mx-sm-0" id="dn-chat-start-conversation-title-error"></div>\
                            </div>' + */

                            '<div class="form-group col-12 px-0 mb-0">' +

                                (
                                    dn.constants.UseRichTextEditor 
                                        ? '<div class="dn-chat-start-conversation-body" id="dn-chat-start-conversation-body"></div><div class="invalid-feedback mx-1 mx-sm-0" id="dn-chat-start-conversation-body-error"></div>' 
                                        : '<textarea class="dn-chat-start-conversation-body ' + dn.constants.Classnames.TextOnlyEditor + '" id="dn-chat-start-conversation-body" style="padding: 10px; display: inline-block; width: 100%;" /><div class="invalid-feedback mx-1 mx-sm-0" id="dn-chat-start-conversation-body-error"></div>'
                                ) +

                            '</div>' + 

                            // Error Display
                            '<div class="mt-2 mr-0 alert alert-danger alert-dismissible fade show dn-chat-start-conversation-error" style="display: none" role="alert">\
                                <span class="dn-chat-start-conversation-error-message" id="dn-chat-start-conversation-error"></span>\
                                <button type="button" class="close" data-dismiss="alert" aria-label="Close">\
                                <span aria-hidden="true">&times;</span>\
                                </button>\
                            </div>\
                        </div>\
                        <p id="closing-modal"></p>\
                    </div>\
                    <div class="modal-footer" style="background-color: #f8f9fa; border-top: none">\
                        <div class="dn-chat-start-attachments"></div>\
                        <button type="button" class="btn dn-button dn-chat-start-conversation-attach" id="dn-chat-start-conversation-attach"><i class="fal fa-paperclip"></i></button>\
                        <button type="button" class="btn btn-primary dn-chat-start-conversation-send-button" id="dn-chat-start-conversation-send-button" disabled="disabled"><i class="fas fa-arrow-up"></i> Send</button>\
                    </div>\
                </div>\
            </div>\
        </div>'));

        // stop any spinners before showing our new one
        dn.Spin(false);

        // remember the modals that were already open before us, so we can show them when done. 
        var openModals = $('.modal.show');
        openModals.modal('hide');

        var id = '#dn-chat-start-conversation';
        $(id).modal();

        // Close button clicked
        $('.dn-chat-start-conversation-close').click
        (
            function(event) 
            {
                openModals.modal('show'); // re-show modals that were open when we first appeared

                if (opt.onClose)
                {
                    opt.onClose();
                }
            }
        );

        // Send button clicked
        $('#dn-chat-start-conversation-send-button').click
        (
            function()
            {
                // This method actually submits the new message to our API, which will either
                // create an entirely new conversation (if none exists for the participants),
                // or it will just add the message to an existing conversation, and return that.
                chat.UpsertConversation
                (
                    {
                        Recipients: dn.text.Split($('#dn-chat-start-conversation-user-search').val(), ","),
                        FileKeys: (chat.Editors[editorBodyKey].FileKeys || []),
                        FileTypes: (chat.Editors[editorBodyKey].FileTypes || []),
                        Body: (dn.constants.UseRichTextEditor ? (chat.Editors[editorBodyKey].getData() || null) : (chat.Editors[editorBodyKey].val() || null)),
                        OnSuccess: function(result)
                        {
                            // Hide the PM modal dialog
                            $(id).modal('hide');

                            if (opt.onSuccess) 
                            {
                                opt.onSuccess(result.Message);
                            }

                            // Insert/replace the new conversation
                            chat.InsertConversation(result.Message);
                            chat.ShowConversation(result.Message.DiscussionKey, result.Message.MessageKey);
                        }
                    }
                );
            }
        );
        
        if (dn.constants.UseRichTextEditor)
        {
            dn.LoadRichTextEditor
            (
                function()
                {
                    console.debug("chat.StartConversation", "dn.LoadRichTextEditor", "onComplete Executed");

                    // set up CKEditor
                    BalloonEditor.create
                    (
                        $("#dn-chat-start-conversation-body")[0],
                        // Merge in our custom placeholder for this CKEditor instance
                        Object.assign
                        (
                            {
                                placeholder: (dn.chat.IsRobotConversation ? "Your message to " + dn.chat.Type.Robot.Name : "Your private message")
                            }, 
                            dn.constants.HtmlEditorConfig
                        )
                    )
                    .then(function (editor) 
                    {
                        chat.Editors["dn-chat-start-conversation-body"] = editor;
                        editor.setData(opt.body || '');

                        //Move the cursor to the end though we don't want to modify the cursor until after CK has
                        // finished setting the data otherwise it puts the cursor at the start.
                        setTimeout(
                            function(){
                                chat.Editors["dn-chat-start-conversation-body"].model.change(function(writer) {
                                    writer.setSelection( writer.createPositionAt( editor.model.document.getRoot(), 'end'));
                                } );
                            }, 
                            50);
                    })
                    .catch(function (error) 
                    {
                        console.error("chat.StartConversation", error);
                    });
                }
            );
        }
        else
        {
            chat.Editors["dn-chat-start-conversation-body"] = $("#dn-chat-start-conversation-body");
            $("#dn-chat-start-conversation-body").val(opt.body || '');
        }

        // setup autosuggest for recipients
        if (dn.chat.IsRobotConversation)
        {
            // This chat type only allows talking to one user, the robot.
            // dn.autosuggest.UserSetup
            // (
            //     {
            //         selector: '#dn-chat-start-conversation-user-search',
            //         prefilledUsers: [dn.chat.Type.Robot.UserKey],
            //         suggestExisting: true,
            //         suggestedUsers: [dn.chat.Type.Robot],
            //         onChange: checkEnableSendPrivateMessageButton,
            //         maxItems: 1,
            //         plugins: []
            //     }
            // );

            // Go with a read-only "to" field.
            $("#dn-chat-start-conversation .recipient-field").replaceWith('<div class="form-control faux-selectize-input">\
                <input type="hidden" id="dn-chat-start-conversation-user-search" value="' + dn.chat.Type.Robot.UserKey + '">\
                <div class="dn-user" id="dn-chat-start-conversation-from"><img class="dn-user-avatar" src="' + dn.chat.Type.Robot.UrlProfilePhoto + '">' + dn.chat.Type.Robot.Name + '</div>\
            </div>');
        }
        else
        {
            // Non-bot specific chat type
            $("#dn-chat-start-conversation .recipient-field").replaceWith('<input name="nameNotSearch" type="text" id="dn-chat-start-conversation-user-search" class="dn-chat-start-conversation-user-search form-control col-12" aria-label="Names of members to message" placeholder="Names of members to message" autocomplete="off" autocorrect="off" autocapitalize="off" spellcheck="false" class="form-control dn-compose-users dn-autosuggest-field" data-role="tagsinput">');

            dn.autosuggest.UserSetup
            (
                {
                    selector: '#dn-chat-start-conversation-user-search',
                    prefilledUsers: opt.prefilledUsers,
                    suggestExisting: true,
                    suggestedUsers: opt.suggestedUsers,
                    onChange: checkEnableSendPrivateMessageButton
                }
            );
        }

        // It's showtime!
        $(id).modal('show');

        checkEnableSendPrivateMessageButton();

        // Setup the actual system for attachments
        setupAttachmentControls
        (
            $('#dn-chat-start-conversation-attach'), 
            editorBodyKey, 
            ".dn-chat-start-attachments"
        );

        // Not sure why, but this never seems to work unless we use setTimeout to delay this
        setTimeout
        (
            function()
            {
                $(".dn-chat-start-conversation " + dn.constants.EditorSelector)
                .off("keydown paste cut input")  // Re: https://github.com/dennyferra/TypeWatch/issues/36#issuecomment-722637558
                .typeWatch
                (
                    {
                        callback: checkEnableSendPrivateMessageButton,
                        wait: dn.chat.WaitForTyping,
                        highlight: false,
                        allowSubmit: false,
                        captureLength: 0
                    }
                );

                if (opt.prefilledUsers)
                {
                    // Recipients were supplied, so put them in the body field
                    $("#dn-chat-start-conversation-body").focus();
                }
                else 
                {
                    // No recipients supplied, so put the focus in the recipients field.
                    $("#dn-chat-start-conversation-user-search-selectized").focus();
                }
            },
            0
        );
    };

    /**
     * When we insert messages, we call this method to kick off our process for 
     * listening for changes in the viewport size, and smoothly updating the position
     * of the viewport scrollTop to maintain the current position.
     * 
     * Here's what happens when this is called:
     * 
     * 1. The `chat.ResizingTimer` flag gets set. As long as this is non-zero, the 
     *    chat.AdjustScrollPosition method will execute completely, which results in 
     *    a check for the height changing in the viewport, and adjusting the scrollTop 
     *    position to keep the viewport from bouncing around.
     * 
     * 2. A 10 second timer is set, which will call chat.StopResizing() if it executes.
     * 
     * 3. Each time the height of the viewport changes (while `chat.IsResizing` is true),
     *    the timer is reset to 10 seconds again, the idea being it won't run until 10
     *    seconds after the last resize event. 
     * 
     * This behavior should guarantee that the `requestAnimationFrame` method is not
     * called when we don't need to call it. 
     */
    chat.StartResizing = function()
    {
        console.debug("chat.StartResizing");

        if (chat.ResizingTimer !== 0)
        {
            clearTimeout(chat.ResizingTimer);
        }

        chat.ResizingTimer = setTimeout(chat.StopResizing, chat.MillisecondsOfResizing);
        requestAnimationFrame(chat.AdjustScrollPosition);
    };

    /**
     * Clears any ResizingTimer that may have been set previously in `StartResizing`.
     */
    chat.StopResizing = function()
    {
        console.debug("chat.StopResizing");

        if (chat.ResizingTimer !== 0)
        {
            clearTimeout(chat.ResizingTimer);
        }

        chat.ResizingTimer = 0;
    };


    /**
     * This is called when the user is typing, so that we can potentially notify
     * other participants. Once the typing indication is sent, it will go away if
     * "too much" time passes, or a new reply is inserted by the user.
     * 
     * TODO: Implement this, likely using WebSockets.
     * 
     * @param {string}  discussionKey - The root messagekey of the conversation being replied to.
     */
    chat.Typing = function(discussionKey, debugging)
    {
        console.debug("chat.Typing", discussionKey, debugging);

        dn.realtime.SendMessage
        (
            {
                Category: dn.constants.EventCategory.Chat,
                Action: dn.constants.EventAction.Typed,
                Data: 
                {
                    Message: 
                    {
                        DiscussionKey: discussionKey
                    }
                }
            }
        );
    };

    /**
     * This method actually submits the new message to our API, which will either
     * create an entirely new conversation (if none exists for the participants),
     * or it will just add the message to an existing conversation, and return that.
     * 
     * Once that is done, we insert the new conversation into the display, and then
     * show the conversation to the user.
     */
    chat.UpsertConversation = function(opt) 
    {
        dn.Spin(true);
        $(".dn-chat-start-conversation-send-button").prop("disabled", true);

        var message = 
        {
            FileKeys: opt.FileKeys,
            FileTypes: opt.FileTypes,
            Recipients: opt.Recipients,
            ParentKey: opt.ParentKey,
            TypeId: chat.Type.TypeId,
            Body: opt.Body,
            DisableRichTextEditing: !dn.constants.UseRichTextEditor,
            Format: (dn.constants.UseRichTextEditor ? "Html" : "Plain")
        };

        $.postJSON("/api/v1/chat", message)
        .always
        (
            function () 
            {
                dn.Spin(false);
                $(".dn-chat-start-conversation-send-button").prop("disabled", false);
            }
        )
        .done
        (
            function (result) 
            {
                console.debug("chat.StartConversation", "Started!", result);

                if (opt.OnSuccess)
                {
                    opt.OnSuccess(result);
                }
            }
        )
        .fail
        (
            function (xhr, textStatus, errorThrown) 
            {
                if (!dn.IsErrorHandledAutomatically(null, xhr, null, errorThrown)) 
                {
                    dn.ShowModal
                    ({
                        title: 'Problem Starting Chat',
                        body: dn.FormatApiError(xhr.responseJSON)
                    });
                }
            }
        );
    };

    $(document).ready
    (
        function ($)
        {
            // Process any queued functions now that the namespace is ready.
            dn.ProcessQueuedFunctions('dn.chat');
        }
    );

}(window.dn.chat = window.dn.chat || {}, jQuery));


